<template>
  <div class="book-export__kindle">
    <h2 class="book-export__action-title">
      <span class="book-export__title-arrow iconfont iarrow"></span>Send to my
      Kindle device
    </h2>

    <p class="book-export__action-description">
      Read on your favorite Kindle device with all Kindle features supported.
    </p>

    <div
      class="book-export__kindle-item book-export__kindle-header"
      @click="toggleExpand"
    >
      <span class="book-export__kindle-title">How to integrate</span>
      <button class="button-toggle-expand">
        <span
          v-if="isExpanded"
          class="book-export__expanded-icon iconfont ikindle-collapse"
        ></span>
        <span v-else class="book-export__expanded-icon iconfont ikindle-expand">
        </span>
      </button>
    </div>

    <div v-if="isExpanded" class="book-export__kindle-item">
      <div class="book-export__kindle-copy-instruction">
        <strong>Step 1</strong>: Copy our verified address
      </div>
      <div class="book-export__copy-email">
        <input
          class="book-export__kindle-input"
          type="text"
          readonly
          value="export@shortform.com"
        />
        <button
          v-if="supportsCopy"
          class="book-export__copy-button"
          @click="copy"
        >
          <span class="book-export__copy-icon iconfont ikindle-copy"></span>
          <CopyMessagePopup :show="showCopyNotification" />
        </button>
      </div>
    </div>

    <div v-if="isExpanded" class="book-export__kindle-item">
      <strong class="book-export__kindle-item-title">Step 2:</strong>
      <p class="book-export__kindle-row">
        > Go to your
        <a
          class="book-export__kindle-link book-export__preference-link"
          href="https://www.amazon.com/hz/mycd/myx#/home/settings/payment#pdoc"
          target="_blank"
          rel="noopener noreferrer"
          >Kindle preferences page</a
        >.
      </p>
      <p class="book-export__kindle-row">
        > Expand "<strong>Personal Document Settings</strong>".
      </p>
      <p class="book-export__kindle-row">
        > Click the "<strong>Add a new approved e-mail address</strong>" link.
      </p>
      <p class="book-export__kindle-row">
        > Add <strong>export@shortform.com</strong> to your approved e-mail
        list.
      </p>
      <p class="book-export__kindle-row book-export__kindle-hint">
        Open
        <button
          class="book-export__kindle-link book-export__button-screenshot"
          @click="toggleScreenshot"
        >
          screenshot
        </button>
        for help
      </p>
    </div>

    <div v-if="isExpanded" class="book-export__kindle-item">
      <strong class="book-export__kindle-item-title">Step 3:</strong>
      <p class="book-export__kindle-row">
        > Go to your
        <a
          class="book-export__kindle-link book-export__preference-link"
          href="https://www.amazon.com/hz/mycd/myx#/home/settings/payment#pdoc"
          target="_blank"
          rel="noopener noreferrer"
          >Kindle preferences page</a
        >.
      </p>
      <p class="book-export__kindle-row">
        > Expand "<strong>Personal Document Settings</strong>".
      </p>
      <p class="book-export__kindle-row">
        > Find the "<strong>Send-to-Kindle E-Mail Settings</strong>" section.
      </p>
      <p class="book-export__kindle-row">
        > Copy and paste your personal Kindle email to the field below.
      </p>
    </div>

    <form role="form" class="form" novalidate @submit.prevent="sendToBackend">
      <div class="book-export__kindle-email-box">
        <div class="book-export__kindle-label-wrapper">
          <label class="book-export__kindle_subtitle" for="kindleEmail">
            Your personal Kindle email
          </label>
          <FormError :errors="validation.errors" field="kindle_email" />
        </div>
        <InputWithContextMenu
          ref="kindle_email"
          v-model="kindleEmail"
          name="kindleEmail"
          type="text"
          placeholder="@kindle.com"
          :class="validation.stateClass('kindle_email')"
        />
      </div>

      <button
        v-wait:disabled="'Sending'"
        class="btns kindle-button"
        type="submit"
        :class="{ 'book-export__disabled': !kindleEmail }"
        :disabled="!kindleEmail"
      >
        <v-wait for="Sending">
          <template v-slot:waiting>
            Sending
            <button-spinner />
          </template>
          Send to Kindle
        </v-wait>
      </button>
    </form>

    <KindleScreenshotPopup
      :show="showScreenshotPopup"
      @toggleShow="toggleScreenshot"
    />
    <KindleNotification
      :show="showNotification"
      @toggleShow="toggleNotification"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeMount, ref } from 'vue'
import { useWait } from '@/app/composables/useWait'

import { Sentry } from '@/services/sentry'

import FormError from '@/components/ui/FormError.vue'
import { ValidationState } from '@/helpers/form'
import { assertNotNull } from '@/helpers/typing'

import { BookView } from '@/models/doc.book'
import { backend } from '@/services/backend'
import InputWithContextMenu from '@/app/components/InputWithContextMenu.vue'
import KindleScreenshotPopup from './KindleScreenshotPopup.vue'
import KindleNotification from './KindleNotification.vue'
import CopyMessagePopup from './CopyMessagePopup.vue'

const props = defineProps<{
  book: BookView
}>()

const wait = useWait()

const validation = ref(new ValidationState())
const isExpanded = ref(true)
const showScreenshotPopup = ref(false)
const showNotification = ref(false)
const showCopyNotification = ref(false)

const shortFormEmail = 'export@shortform.com'

const kindleEmail = ref<string>()

onBeforeMount(() => {
  // If the user already has a kindle_email set, don't show the instructions
  // and fill in the Kindle email form field.
  if (props.book.doc_export_stats && props.book.doc_export_stats.kindle_email) {
    isExpanded.value = false
    kindleEmail.value = props.book.doc_export_stats.kindle_email
  }
  // For now, force the settings block to be expanded, so users
  // see the updated email in the instructions, see #12087.
  isExpanded.value = true
})

function getErrorMessage(error: any): string {
  if (error.response === undefined) {
    Sentry.captureException(error)
    return 'Could not talk to backend. Network or authorisation error?'
  } else {
    // The response structure is that of marshmallow.exceptions.ValidationError:
    //   {errors: {field_name: [error_msg, ...], ...}}
    // field_name may be
    // - `_schema`: default, for a general schema-level error. But we don't
    //   expect any errors like this.
    // - an actual field. For this, the fields we may expect errors from are
    //   `user`, `url_slug`, and `kindle_email`.
    const knownFieldNames = ['_schema', 'user', 'url_slug', 'kindle_email']
    const knownErrorMessages = new Set([
      'Doc export is not available for free users',
      'Doc export is not available during trial',
      'Export limit reached',
      "We didn't find this summary in our database.",
      'The given email does not end with @kindle.com.',
      'No Send to Kindle email given.',
    ])

    // If we find an error message we recognize, don't send the error to
    // Sentry since the user can deal with it.
    const maybeErrors = error?.response?.data?.errors
    if (maybeErrors) {
      for (const fieldName of knownFieldNames) {
        if (fieldName in maybeErrors) {
          for (const errorMessage of maybeErrors[fieldName]) {
            if (knownErrorMessages.has(errorMessage)) {
              return errorMessage
            }
          }
        }
      }
    }

    // Otherwise, it is probably not useful to show the user the specific
    // error, and we should capture it in Sentry instead.
    Sentry.captureException(error)
    return 'Error sending to Kindle. Please contact support.'
  }
}

async function sendToBackend(): Promise<void> {
  try {
    validation.value.reset()
    wait.start('Sending')
    const email = assertNotNull(kindleEmail.value)
    await backend.sendToKindle(props.book.url_slug, email)
    toggleNotification()
  } catch (error) {
    validation.value.showMessage('kindle_email', getErrorMessage(error))
  } finally {
    wait.end('Sending')
  }
}

function toggleExpand(): void {
  isExpanded.value = !isExpanded.value
}

function toggleScreenshot(): void {
  showScreenshotPopup.value = !showScreenshotPopup.value
}

function toggleNotification(): void {
  showNotification.value = !showNotification.value

  if (showNotification.value) {
    setTimeout(() => {
      showNotification.value = false
    }, 5000)
  }
}

const supportsCopy = computed(() => {
  return navigator.clipboard !== undefined
})

function copy(): void {
  navigator.clipboard.writeText(shortFormEmail)
  showCopyNotification.value = !showCopyNotification.value

  setTimeout(() => {
    showCopyNotification.value = false
  }, 2000)
}
</script>
